// schema.org payload for a property sales page, plus the plain-JSON facts
// shape behind /api/stay/<slug>. Single source for three consumers that had
// been drifting apart: Stay.jsx (runtime), scripts/prerender.mjs (build), and
// netlify/functions/stay-facts.js (the machine-readable endpoint).
//
// WHY THE EXTRA MARKUP (north star, 2026-07-25): the goal is to be booked out
// of an AI conversation. An answer engine that can only read prose can say the
// house exists; it cannot say what a stay costs, how long a stay has to be, or
// how to start one. The additions here are exactly the facts that turn "this
// looks nice" into a bookable answer: a nightly range with currency, the
// minimum and maximum stay, check-in and check-out, pets, and a ReserveAction
// whose URL template an agent can fill in and hand to a guest.
//
// INTEGRITY RULE: nothing here may invent a number. Every value is read from
// property-config.js, and the fee/tax total is deliberately NOT stated because
// only a live OwnerRez quote knows it — the payload points at the quote
// endpoint instead of guessing. Same rule as the events feed.

import { propertyPhotoUrls } from './property-config.js';

// Direct rates are set at Airbnb minus this, applied in every quote with no
// code to enter (owner call 2026-07-29 at 5%, lowered to 3% on 2026-09-20).
// It mirrors OwnerRez discount 102723283 ("Direct Booking Discount", percent
// of rent, every source but Airbnb): change that record and this number
// together. Stated in one place so the FAQ copy, the markup, and the API can
// never disagree about the number.
export const DIRECT_DISCOUNT_PCT = 3;

// Deep-link contract for the booking page. Stay.jsx reads these params and
// pre-selects the dates, so an agent can hand a guest a link that lands on a
// priced quote instead of an empty calendar. Kept here because both the
// ReserveAction template and the facts endpoint publish it.
export function bookingUrl(property, origin, { checkin, checkout, adults, children } = {}) {
  const url = new URL(`${origin}/stay/${property.slug}`);
  if (checkin) url.searchParams.set('checkin', checkin);
  if (checkout) url.searchParams.set('checkout', checkout);
  if (adults) url.searchParams.set('adults', String(adults));
  if (children) url.searchParams.set('children', String(children));
  return url.toString();
}

export function bookingUrlTemplate(property, origin) {
  return `${origin}/stay/${property.slug}?checkin={checkin}&checkout={checkout}&adults={adults}`;
}

// Public location: locality, not street. Owner decision 2026-09-21, matching
// what the welcome guide already promises ("the location and code arrive in
// your check-in message") and what Airbnb and Vrbo do, which is to withhold
// the exact address until a reservation exists. property.address keeps the
// street for anything that genuinely needs it; nothing public reads it.
export function postalAddress(property, { exact = false } = {}) {
  const a = property.address || {};
  return {
    '@type': 'PostalAddress',
    ...(exact && a.street ? { streetAddress: a.street } : {}),
    addressLocality: a.locality || 'Mount Pleasant',
    addressRegion: a.region || 'SC',
    ...(a.postalCode ? { postalCode: a.postalCode } : {}),
    addressCountry: a.country || 'US'
  };
}

// Rounded to two decimal places, about a kilometre, which puts the point in
// the right neighbourhood and not on the house.
//
// Rounding rather than a random jitter, deliberately: a random offset differs
// on every request, so asking repeatedly and averaging recovers the true
// point. A rounded coordinate is the same answer every time and cannot be
// averaged away. It is also honestly describable, which a jitter is not.
export const GEO_PRECISION_METERS = 1000;

export function approximateGeo(property) {
  if (!property?.geo) return null;
  const round = (n) => Math.round(n * 100) / 100;
  return {
    latitude: round(property.geo.latitude),
    longitude: round(property.geo.longitude)
  };
}

// Nightly range as a UnitPriceSpecification. min/max rather than a single
// price because that is the truth: rates are seasonal and the quote is
// authoritative. Both anchors have to exist or the offer is left off entirely
// rather than half-stated.
function nightlyOffer(property, origin) {
  if (!property.fromNightly || !property.peakNightly) return null;
  return {
    '@type': 'Offer',
    name: 'Direct booking',
    url: `${origin}/stay/${property.slug}`,
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      priceCurrency: 'USD',
      minPrice: property.fromNightly,
      maxPrice: property.peakNightly,
      unitCode: 'DAY',
      unitText: 'night',
      valueAddedTaxIncluded: false,
      description: `Nightly rate before taxes and fees, which are itemized in the quote. Direct rates are ${DIRECT_DISCOUNT_PCT}% below the same dates on Airbnb, applied automatically with no code to enter${
        property.cleaningFee && property.cleaningFeeAirbnb
          ? `, the cleaning fee is $${property.cleaningFee} against $${property.cleaningFeeAirbnb} on Airbnb`
          : ''
      }, and there is no guest service fee.`
    },
    ...(property.minNights || property.maxNights
      ? {
          eligibleQuantity: {
            '@type': 'QuantitativeValue',
            ...(property.minNights ? { minValue: property.minNights } : {}),
            ...(property.maxNights ? { maxValue: property.maxNights } : {}),
            unitCode: 'DAY',
            unitText: 'night'
          }
        }
      : {}),
    seller: {
      '@type': 'Organization',
      name: 'Charleston.Vacations',
      url: origin
    }
  };
}

// VacationRental is a LodgingBusiness subtype, so checkinTime, petsAllowed,
// amenityFeature, makesOffer, and potentialAction are all in-vocabulary here.
export function vacationRentalJsonLd(property, origin) {
  const images = propertyPhotoUrls(property, origin);
  const offer = nightlyOffer(property, origin);

  return {
    '@context': 'https://schema.org',
    '@type': 'VacationRental',
    // Google requires a stable listing identifier for VacationRental markup
    // (Search Console flags it as critical when absent). The slug is already
    // the property's public ID everywhere else — URLs, /api/stay, the MCP
    // tools — so it is the identifier here too.
    identifier: property.slug,
    additionalType: 'House',
    name: property.name,
    description: property.description,
    ...(images.length ? { image: images } : {}),
    ...(property.reviews?.rating && property.reviews?.count
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: property.reviews.rating,
            reviewCount: property.reviews.count,
            bestRating: 5
          }
        }
      : {}),
    url: `${origin}/stay/${property.slug}`,
    address: postalAddress(property),
    ...(approximateGeo(property)
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            ...approximateGeo(property),
            // Schema has no "this is approximate" flag, so the tolerance is
            // stated in the one field that takes prose.
            description: `Approximate, to about ${GEO_PRECISION_METERS} metres. The exact address is sent after booking.`
          }
        }
      : {}),
    ...(property.checkinTime ? { checkinTime: property.checkinTime } : {}),
    ...(property.checkoutTime ? { checkoutTime: property.checkoutTime } : {}),
    // Both are house rules the guest is agreeing to, and both are the kind of
    // filter question an answer engine gets asked before anything else.
    petsAllowed: false,
    smokingAllowed: false,
    numberOfRooms: property.bedrooms,
    tourBookingPage: `${origin}/stay/${property.slug}`,
    ...(property.amenities?.length
      ? {
          amenityFeature: property.amenities.map((name) => ({
            '@type': 'LocationFeatureSpecification',
            name,
            value: true
          }))
        }
      : {}),
    containsPlace: {
      '@type': 'Accommodation',
      occupancy: {
        '@type': 'QuantitativeValue',
        maxValue: property.sleeps,
        unitText: 'guests',
        // The adult cap is a separate OwnerRez rule and the schema has no
        // field for it, so it rides in the description rather than being
        // left for a crawler to discover by getting a quote refused.
        description: `Up to ${property.sleeps} guests, of whom at most ${property.maxAdults} may be adults and at most ${property.maxChildren} children.`
      },
      numberOfBedrooms: property.bedrooms,
      numberOfBathroomsTotal: property.bathrooms,
      ...(property.beds?.length ? { bed: property.beds.map((b) => `${b.room}: ${b.bed}`) } : {})
    },
    ...(offer ? { makesOffer: offer } : {}),
    // The agent-actionable part: fill the template, land on a priced quote.
    potentialAction: {
      '@type': 'ReserveAction',
      name: 'Book direct',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: bookingUrlTemplate(property, origin),
        httpMethod: 'GET',
        actionPlatform: [
          'https://schema.org/DesktopWebPlatform',
          'https://schema.org/MobileWebPlatform'
        ]
      },
      result: {
        '@type': 'LodgingReservation',
        name: `Stay at ${property.name}`
      }
    }
  };
}
